// api/profile.js

const API_BASE_URL = 'https://emiratesscholar.com/system/api';

// ==================== البيانات الثابتة ====================
const titles = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'];

const countries = [
  { code: 'AE', name: 'United Arab Emirates', phoneCode: '+971' },
  { code: 'US', name: 'United States of America', phoneCode: '+1' },
  { code: 'GB', name: 'United Kingdom', phoneCode: '+44' },
  { code: 'SA', name: 'Saudi Arabia', phoneCode: '+966' },
  { code: 'EG', name: 'Egypt', phoneCode: '+20' },
  { code: 'PK', name: 'Pakistan', phoneCode: '+92' },
  { code: 'IN', name: 'India', phoneCode: '+91' },
  { code: 'CA', name: 'Canada', phoneCode: '+1' },
  { code: 'AU', name: 'Australia', phoneCode: '+61' },
  { code: 'DE', name: 'Germany', phoneCode: '+49' },
  { code: 'FR', name: 'France', phoneCode: '+33' },
  { code: 'JP', name: 'Japan', phoneCode: '+81' },
  { code: 'CN', name: 'China', phoneCode: '+86' },
  { code: 'QA', name: 'Qatar', phoneCode: '+974' },
  { code: 'KW', name: 'Kuwait', phoneCode: '+965' },
  { code: 'BH', name: 'Bahrain', phoneCode: '+973' },
  { code: 'OM', name: 'Oman', phoneCode: '+968' },
  { code: 'JO', name: 'Jordan', phoneCode: '+962' }
];

const sectors = [
  'Academic', 'Research', 'Government', 'Private Sector', 'Non-Profit',
  'Healthcare', 'Technology', 'Education', 'Finance', 'Manufacturing'
];

const researchOptions = [
  'Artificial Intelligence', 'Quality Management', 'Data Science',
  'International Management', 'Technology Acceptance', 'Data Sensing',
  'Project Management', 'Internet Commerce', 'Business Development',
  'Machine Learning', 'Deep Learning', 'Natural Language Processing',
  'Computer Vision', 'Robotics', 'Blockchain', 'Internet of Things',
  'Cloud Computing', 'Cybersecurity', 'Big Data Analytics',
  'Healthcare Technology', 'Biotechnology', 'Renewable Energy',
  'Sustainable Development', 'Environmental Science',
  'Digital Transformation', 'Smart Cities', 'Knowledge Management',
  'Innovation Management', 'Strategic Management', 'Supply Chain Management'
];

// ==================== دوال جلب البيانات الثابتة ====================
export const fetchTitles = async () => {
  return new Promise((resolve) => setTimeout(() => resolve(titles), 100));
};

export const fetchCountries = async () => {
  return new Promise((resolve) => setTimeout(() => resolve(countries), 100));
};

export const fetchSectors = async () => {
  return new Promise((resolve) => setTimeout(() => resolve(sectors), 100));
};

export const fetchResearchOptions = async () => {
  return new Promise((resolve) => setTimeout(() => resolve(researchOptions), 100));
};

// ==================== جلب مواضيع البحث من API ====================
export const fetchResearchTopics = async () => {
  const token = localStorage.getItem('auth_token');

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/paper-topics-list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.topicsList && Array.isArray(data.topicsList)) {
          const topics = data.topicsList
            .filter(topic => topic.status === 'Active')
            .map(topic => topic.title);

          localStorage.setItem('research_topics', JSON.stringify(topics));
          return topics;
        }
      }
    } catch (error) {
      console.error('❌ [fetchResearchTopics] Failed:', error);
    }
  }

  const storedTopics = localStorage.getItem('research_topics');
  if (storedTopics) {
    try { return JSON.parse(storedTopics); } catch { }
  }

  return researchOptions;
};

// ==================== دوال مساعدة للتنسيق ====================
const formatYears = (startDate, endDate) => {
  if (!startDate) return '';
  const start = startDate.split(' ')[0];
  if (!endDate || endDate === 'null' || endDate === null) return `${start} – Present`;
  const end = endDate.split(' ')[0];
  return `${start} – ${end}`;
};

const formatWorkYears = (startDate, endDate, status) => {
  if (!startDate) return '';
  if (!endDate || status === 'Active' || status === 'Present') return `${startDate} – Present`;
  return `${startDate} – ${endDate}`;
};

const calculateYearlyStats = (publications) => {
  if (!publications || publications.length === 0) return null;
  const yearCounts = {};
  const currentYear = new Date().getFullYear();

  publications.forEach(pub => {
    if (pub.date) {
      const year = new Date(pub.date).getFullYear();
      if (year && year >= currentYear - 4) {
        yearCounts[year] = (yearCounts[year] || 0) + 1;
      }
    }
  });

  const labels = [], values = [];
  for (let i = 4; i >= 0; i--) {
    const year = currentYear - i;
    labels.push(year.toString());
    values.push(yearCounts[year] || 0);
  }

  const colors = ['#5DCAA5', '#85B7EB', '#7F77DD', '#F0997B', '#FAC775'];
  const hoverColors = ['#1D9E75', '#378ADD', '#534AB7', '#D85A30', '#EF9F27'];

  return { labels, values, colors, hoverColors };
};

// ==================== دالة تحويل بيانات API إلى هيكل الملف الشخصي ====================
const mapApiResponseToProfile = (apiResponse) => {
  const profile = apiResponse?.Profile || {};
  const details = apiResponse?.UserDetail || {};
  const educationData = apiResponse?.userEducation || [];
  const experienceData = apiResponse?.userExperience || [];

  const createdYear = profile?.created_at ? new Date(profile.created_at).getFullYear() : new Date().getFullYear();
  const title = details?.title ? details.title.charAt(0).toUpperCase() + details.title.slice(1) : '';

  const education = educationData.map(edu => ({
    id: edu.id, degree: edu.mainCourse || '', university: edu.school || '',
    field: edu.field || '', country: edu.country || '',
    startDate: edu.startedIn ? edu.startedIn.split(' ')[0] : '',
    endDate: edu.completedin ? edu.completedin.split(' ')[0] : '',
    years: formatYears(edu.startedIn, edu.completedin)
  }));

  const experience = experienceData.map(exp => ({
    id: exp.id, position: exp.position || '', company: exp.company || '',
    type: exp.type || '', location: exp.location || '',
    description: exp.description || '', startDate: exp.startDate || '',
    endDate: exp.endDate || '',
    isCurrent: !exp.endDate || exp.status === 'Active' || exp.status === 'Present',
    status: exp.status || '',
    years: formatWorkYears(exp.startDate, exp.endDate, exp.status)
  }));

  const researchAreas = details?.field ? details.field.split(',').map(f => f.trim()).filter(f => f) : [];

  return {
    title, firstName: profile?.name || '', lastName: profile?.lname || '',
    email: profile?.email || '', country: details?.country || details?.code || '',
    phoneCode: details?.code || '', phone: details?.contact || '',
    position: details?.profession || '', company: details?.organization || '',
    sector: details?.sector || '', bio: details?.about || '',
    avatar: details?.imageLink || apiResponse?.userImage || '',
    coverImage: details?.cover || '', memberSince: createdYear,
    esid: `ESID ${details?.membership || ''} ${createdYear}`,
    membership: details?.membership || '', membershipExpiry: details?.expiry || '',
    city: details?.city || '', address: details?.address || '',
    postal: details?.postal || '', gender: details?.gender || '',
    nationality: details?.nationality || '', dob: details?.dob || details?.dateOfBirth || '',
    passportNumber: details?.passportNumber || details?.passport_number || '',
    eidNumber: details?.eid_number || details?.emiratesIdNumber || '',
    isResident: details?.is_resident || details?.isUaeResident || '',
    isUaeVisaHolder: details?.uaeVisa || details?.isUaeVisaHolder || details?.is_uae_visa_holder || details?.is_visa_holder || '',
    passportExpiry: details?.passportExpiry || details?.passport_expiry || '',
    passportIssue: details?.passportIssue || details?.passport_issue || '',
    passportIssuePlace: details?.passportIssuePlace || details?.passport_issue_place || '',
    religion: details?.religion || '',
    subReligion: details?.subReligion || details?.sub_religion || '',
    maritalStatus: details?.maritalStatus || details?.marital_status || '',
    emiratesExpiryDate: details?.emiratesExpiryDate || details?.emirates_expiry_date || '',
    zipcode: details?.zipcode || details?.postal || '',
    languagesSpoken: details?.languagesSpoken || details?.languages_spoken || '',
    stats: { publications: 0, memberSince: createdYear, hIndex: 0 },
    chartData: { labels: [], values: [], colors: ['#5DCAA5', '#85B7EB', '#7F77DD', '#F0997B', '#FAC775'], hoverColors: ['#1D9E75', '#378ADD', '#534AB7', '#D85A30', '#EF9F27'] },
    education, experience, publications: [], researchAreas,
    documents: (() => {
      let documentsData = apiResponse?.uploadDocument ||
        apiResponse?.Profile?.uploadDocument ||
        details?.uploadDocument ||
        apiResponse?.documents ||
        [];

      const documents = (documentsData || []).map(doc => ({
        id: doc.id,
        name: doc.name || 'Unknown Document',
        link: doc.document_link || doc.link || ''
      }));

      // Fallback to local storage speaker data cache if documents is empty
      if (documents.length === 0) {
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key === 'speaker_data' || key.startsWith('speaker_data_'))) {
              const cached = JSON.parse(localStorage.getItem(key));
              if (cached?.profile?.documents && Array.isArray(cached.profile.documents)) {
                cached.profile.documents.forEach(d => {
                  if (!documents.some(existing => existing.id === d.id)) {
                    documents.push({
                      id: d.id,
                      name: d.name || 'Unknown Document',
                      link: d.link || d.document_link || ''
                    });
                  }
                });
              }
            }
          }
        } catch (e) {
          console.warn('[mapApiResponseToProfile] Failed to fallback to speaker cache:', e);
        }
      }
      return documents;
    })()
  };
};

// ==================== جلب الملف الشخصي ====================
export const fetchProfile = async () => {
  const token = localStorage.getItem('auth_token');

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/new-user-profile`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('profile_data', JSON.stringify(data));
        localStorage.setItem('user_profile', JSON.stringify(data.Profile || {}));
        localStorage.setItem('user_details', JSON.stringify(data.UserDetail || {}));
        localStorage.setItem('profile_education', JSON.stringify(data.userEducation || []));
        localStorage.setItem('profile_experience', JSON.stringify(data.userExperience || []));
        return mapApiResponseToProfile(data);
      }
    } catch (error) {
      console.error('❌ [fetchProfile] Failed:', error);
    }
  }

  const storedData = localStorage.getItem('profile_data');
  if (storedData) {
    try { return mapApiResponseToProfile(JSON.parse(storedData)); } catch { }
  }

  throw new Error('No profile data found');
};

// ==================== جلب المنشورات ====================
export const fetchPublications = async () => {
  const token = localStorage.getItem('auth_token');

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/portal-user-all-submissions`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });

      if (response.ok) {
        const text = await response.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) return { publications: [], yearlyStats: null };

        const data = JSON.parse(text);
        let publicationsList = [];
        if (Array.isArray(data)) publicationsList = data;
        else if (data?.allSubmissions?.data) publicationsList = data.allSubmissions.data;
        else if (data?.data) publicationsList = data.data;
        else if (data?.submissions) publicationsList = data.submissions;

        const publications = publicationsList.map(pub => ({
          id: pub.id, title: pub.title_paper || pub.title || '',
          authors: pub.authors || '', journal: pub.journal_name || '',
          date: pub.created_at || '', status: pub.status || '', type: pub.paper_type || ''
        }));

        return { publications, yearlyStats: calculateYearlyStats(publications) };
      }
    } catch (error) {
      console.error('❌ [fetchPublications] Failed:', error);
    }
  }

  return { publications: [], yearlyStats: null };
};

// ==================== تحديث الملف الشخصي ====================
export const updateProfile = async (data) => {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();

  // All fields
  if (data.title) formData.append('title', data.title);
  if (data.firstName) formData.append('name', data.firstName);
  if (data.lastName) formData.append('lname', data.lastName);
  if (data.email) formData.append('email', data.email); // Added email
  if (data.countryCode) formData.append('code', data.countryCode);
  if (data.phone) formData.append('contact', data.phone);
  if (data.position) formData.append('profession', data.position);
  if (data.company) formData.append('organization', data.company);
  if (data.country) formData.append('country', data.country);
  if (data.city) formData.append('city', data.city);
  if (data.address) formData.append('address', data.address);
  if (data.zipcode) formData.append('postal', data.zipcode);
  if (data.gender !== undefined) formData.append('gender', data.gender);
  if (data.religion !== undefined) formData.append('religion', data.religion);
  if (data.subReligion !== undefined) formData.append('sub_religion', data.subReligion);
  if (data.maritalStatus !== undefined) formData.append('maritalStatus', data.maritalStatus);
  if (data.passportNumber !== undefined) formData.append('passportNumber', data.passportNumber);
  if (data.passportExpiry !== undefined) formData.append('passportExpiry', data.passportExpiry); // Fixed
  if (data.passportIssue !== undefined) formData.append('passportIssue', data.passportIssue); // Fixed
  if (data.passportIssuePlace !== undefined) formData.append('passportIssuePlace', data.passportIssuePlace);
  if (data.bio !== undefined) formData.append('about', data.bio);
  if (data.dob !== undefined) formData.append('dob', data.dob);
  if (data.sector !== undefined) formData.append('sector', data.sector);
  if (data.isUaeVisaHolder !== undefined) {
    formData.append('isUaeVisaHolder', data.isUaeVisaHolder);
    formData.append('uaeVisa', data.isUaeVisaHolder);
  }
  if (data.isResident !== undefined) {
    formData.append('is_resident', data.isResident);
    formData.append('isResident', data.isResident);
  }
  if (data.eidNumber !== undefined) {
    formData.append('eid_number', data.eidNumber);
    formData.append('eidNumber', data.eidNumber);
  }
  if (data.emiratesExpiryDate !== undefined) {
    formData.append('eid_expiry_date', data.emiratesExpiryDate);
    formData.append('emiratesExpiryDate', data.emiratesExpiryDate);
  }
  if (data.researchAreas) {
    const researchStr = Array.isArray(data.researchAreas) ? data.researchAreas.join(',') : data.researchAreas;
    formData.append('field', researchStr);
    formData.append('researchAreas', researchStr);
  }
  if (data.nationality !== undefined) formData.append('nationality', data.nationality);
  if (data.languagesSpoken !== undefined) formData.append('languagesSpoken', data.languagesSpoken);
  if (data.imageFile) formData.append('image', data.imageFile);

  if (!token) {
    updateLocalStorage(data);
    return { success: true, message: 'Updated locally (offline mode)' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/profile-updated`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: formData
    });

    // Parse response
    let result;
    const responseText = await response.text();

    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch {
      throw new Error('Invalid server response');
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication expired. Please login again.');
      }
      if (response.status === 404) {
        throw new Error('Profile endpoint not found. Please check API URL.');
      }
      throw new Error(result.message || result.error || `Update failed (${response.status})`);
    }

    // Update localStorage with new data
    if (result.Profile) {
      localStorage.setItem('user_profile', JSON.stringify(result.Profile));
    }
    if (result.UserDetail) {
      localStorage.setItem('user_details', JSON.stringify(result.UserDetail));
    }
    if (result.userEducation) {
      localStorage.setItem('profile_education', JSON.stringify(result.userEducation));
    }
    if (result.userExperience) {
      localStorage.setItem('profile_experience', JSON.stringify(result.userExperience));
    }

    // Update full profile data
    const fullData = {
      Profile: result.Profile || JSON.parse(localStorage.getItem('user_profile') || '{}'),
      UserDetail: result.UserDetail || JSON.parse(localStorage.getItem('user_details') || '{}'),
      userEducation: result.userEducation || JSON.parse(localStorage.getItem('profile_education') || '[]'),
      userExperience: result.userExperience || JSON.parse(localStorage.getItem('profile_experience') || '[]'),
      userImage: result.userImage || ''
    };
    localStorage.setItem('profile_data', JSON.stringify(fullData));

    return result;

  } catch (error) {
    console.error('❌ [updateProfile] Failed:', error);
    // Fallback to local update
    updateLocalStorage(data);
    throw error;
  }
};

// ==================== التعليم ====================
export const fetchEducation = async () => {
  try { return JSON.parse(localStorage.getItem('profile_education') || '[]'); } catch { return []; }
};

export const addEducation = async (data) => {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('startedIn', data.startDate);
  formData.append('completedin', data.endDate || '');
  formData.append('school', data.university);
  formData.append('field', data.field);
  formData.append('mainCourse', data.degree);
  formData.append('country', data.country);

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/add-user-education`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const education = JSON.parse(localStorage.getItem('profile_education') || '[]');
        education.push({ id: result.id || Date.now(), user_id: result.user_id || 2, startedIn: data.startDate, completedin: data.endDate || null, school: data.university, field: data.field, mainCourse: data.degree, country: data.country, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
        localStorage.setItem('profile_education', JSON.stringify(education));
        return result;
      }
    } catch (error) { console.error('❌ [addEducation] Failed:', error); }
  }

  const education = JSON.parse(localStorage.getItem('profile_education') || '[]');
  const newEducation = { id: Date.now(), user_id: 2, startedIn: data.startDate, completedin: data.endDate || null, school: data.university, field: data.field, mainCourse: data.degree, country: data.country, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
  education.push(newEducation);
  localStorage.setItem('profile_education', JSON.stringify(education));
  return { success: true, id: newEducation.id };
};

export const updateEducation = async (id, data) => {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('startedIn', data.startDate);
  formData.append('completedin', data.endDate || '');
  formData.append('school', data.university);
  formData.append('field', data.field);
  formData.append('mainCourse', data.degree);
  formData.append('country', data.country);

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/update-education/${id}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) { updateLocalEducation(id, data); return await response.json(); }
    } catch (error) { console.error('❌ [updateEducation] Failed:', error); }
  }
  updateLocalEducation(id, data);
  return { success: true };
};

export const deleteEducation = async (id) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-user-education-history/${id}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) {
        const education = JSON.parse(localStorage.getItem('profile_education') || '[]');
        localStorage.setItem('profile_education', JSON.stringify(education.filter(e => e.id !== id)));
        return await response.json();
      }
    } catch (error) { console.error('❌ [deleteEducation] Failed:', error); }
  }
  const education = JSON.parse(localStorage.getItem('profile_education') || '[]');
  localStorage.setItem('profile_education', JSON.stringify(education.filter(e => e.id !== id)));
  return { success: true };
};

// ==================== الخبرات ====================
export const fetchExperience = async () => {
  try { return JSON.parse(localStorage.getItem('profile_experience') || '[]'); } catch { return []; }
};

export const addExperience = async (data) => {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('startDate', data.startDate);
  formData.append('endDate', data.isCurrent ? '' : (data.endDate || ''));
  formData.append('type', data.type || 'Full Time');
  formData.append('location', data.location || '');
  formData.append('description', data.description || '');
  formData.append('status', data.isCurrent ? 'Active' : 'Completed');
  formData.append('position', data.position);
  formData.append('company', data.company);

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/add-work`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) {
        const result = await response.json();
        const experience = JSON.parse(localStorage.getItem('profile_experience') || '[]');
        experience.push({ id: result.id || Date.now(), user_id: result.user_id || 2, startDate: data.startDate, endDate: data.isCurrent ? null : (data.endDate || null), type: data.type, location: data.location, description: data.description, position: data.position, company: data.company, status: data.isCurrent ? 'Active' : 'Completed', created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
        localStorage.setItem('profile_experience', JSON.stringify(experience));
        return result;
      }
    } catch (error) { console.error('❌ [addExperience] Failed:', error); }
  }

  const experience = JSON.parse(localStorage.getItem('profile_experience') || '[]');
  const newExperience = { id: Date.now(), user_id: 2, startDate: data.startDate, endDate: data.isCurrent ? null : (data.endDate || null), type: data.type, location: data.location, description: data.description, position: data.position, company: data.company, status: data.isCurrent ? 'Active' : 'Completed', created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
  experience.push(newExperience);
  localStorage.setItem('profile_experience', JSON.stringify(experience));
  return { success: true, id: newExperience.id };
};

export const updateExperience = async (id, data) => {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('startDate', data.startDate);
  formData.append('endDate', data.isCurrent ? '' : (data.endDate || ''));
  formData.append('type', data.type || 'Full Time');
  formData.append('location', data.location || '');
  formData.append('description', data.description || '');
  formData.append('status', data.isCurrent ? 'Active' : 'Completed');
  formData.append('position', data.position);
  formData.append('company', data.company);

  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/update-work/${id}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) { updateLocalExperience(id, data); return await response.json(); }
    } catch (error) { console.error('❌ [updateExperience] Failed:', error); }
  }
  updateLocalExperience(id, data);
  return { success: true };
};

export const deleteExperience = async (id) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-user-work-history/${id}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) {
        const experience = JSON.parse(localStorage.getItem('profile_experience') || '[]');
        localStorage.setItem('profile_experience', JSON.stringify(experience.filter(e => e.id !== id)));
        return await response.json();
      }
    } catch (error) { console.error('❌ [deleteExperience] Failed:', error); }
  }
  const experience = JSON.parse(localStorage.getItem('profile_experience') || '[]');
  localStorage.setItem('profile_experience', JSON.stringify(experience.filter(e => e.id !== id)));
  return { success: true };
};

// ==================== دوال مساعدة للتحديث المحلي ====================
const updateLocalStorage = (data) => {
  const storedProfile = localStorage.getItem('user_profile');
  if (storedProfile) {
    const userProfile = JSON.parse(storedProfile);
    if (data.firstName) userProfile.name = data.firstName;
    if (data.lastName) userProfile.lname = data.lastName;
    if (data.email) userProfile.email = data.email;
    localStorage.setItem('user_profile', JSON.stringify(userProfile));
  }

  const storedDetails = localStorage.getItem('user_details');
  if (storedDetails) {
    const userDetails = JSON.parse(storedDetails);
    if (data.title) userDetails.title = data.title.toLowerCase();
    if (data.phone) userDetails.contact = data.phone;
    if (data.phoneCode) userDetails.code = data.phoneCode;
    if (data.position) userDetails.profession = data.position;
    if (data.company) userDetails.organization = data.company;
    if (data.sector) userDetails.sector = data.sector;
    if (data.bio) userDetails.about = data.bio;
    if (data.country) userDetails.country = data.country;
    if (data.city) userDetails.city = data.city;
    if (data.address) userDetails.address = data.address;
    if (data.avatar) userDetails.imageLink = data.avatar;
    if (data.researchAreas) userDetails.field = Array.isArray(data.researchAreas) ? data.researchAreas.join(',') : data.researchAreas;
    localStorage.setItem('user_details', JSON.stringify(userDetails));
  }
};

const updateLocalEducation = (id, data) => {
  const education = JSON.parse(localStorage.getItem('profile_education') || '[]');
  const index = education.findIndex(e => e.id === id);
  if (index !== -1) {
    education[index] = { ...education[index], startedIn: data.startDate, completedin: data.endDate || null, school: data.university, field: data.field, mainCourse: data.degree, country: data.country, updated_at: new Date().toISOString() };
    localStorage.setItem('profile_education', JSON.stringify(education));
  }
};

const updateLocalExperience = (id, data) => {
  const experience = JSON.parse(localStorage.getItem('profile_experience') || '[]');
  const index = experience.findIndex(e => e.id === id);
  if (index !== -1) {
    experience[index] = { ...experience[index], startDate: data.startDate, endDate: data.isCurrent ? null : (data.endDate || null), type: data.type, location: data.location, description: data.description, position: data.position, company: data.company, status: data.isCurrent ? 'Active' : 'Completed', updated_at: new Date().toISOString() };
    localStorage.setItem('profile_experience', JSON.stringify(experience));
  }
};

// ==================== رفع مستندات الملف الشخصي ====================
export const uploadProfileDocument = async (file, fileName) => {
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('fileName', fileName);
  formData.append('file_link', file);

  // Fallbacks for compatibility
  formData.append('name', fileName);
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/user-upload-document`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    },
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Upload failed (${response.status})`;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch { }
    throw new Error(errorMessage);
  }

  return await response.json();
};
// ==================== External publications (published elsewhere) ====================
const publicationRequest = async (path, options = {}) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json', ...(options.headers || {}) }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Laravel validation errors: { message, errors: { field: [msg] } }
    const error = new Error(data.message || data.error || `Request failed (${response.status})`);
    error.fieldErrors = data.errors || {};
    error.status = response.status;           // 409 = paper already on the profile
    error.duplicate = data.duplicate || null;
    throw error;
  }
  return data;
};

// Only the fields the profile form edits; others (abstract, pages, PDF) are left untouched on update
const publicationFormData = (pub) => {
  const formData = new FormData();
  ['title', 'authors', 'journal_conference', 'publisher', 'publication_date', 'volume', 'issue', 'url']
    .forEach((key) => formData.append(key, (pub[key] ?? '').toString().trim()));
  // How the paper was added (manual / doi / orcid / scholar); shown as its source
  if (pub.source) formData.append('source', pub.source);
  return formData;
};

export const fetchMyPublications = async () => {
  const data = await publicationRequest('my-publications');
  // Papers published through Emirates Scholar (read-only) first, then those added by the member;
  // the server already lists each paper only once.
  return [...(data.emiratesScholarPapers || []), ...(data.publicationHistory || [])];
};

export const addMyPublication = async (pub) => {
  const data = await publicationRequest('add-my-publication', { method: 'POST', body: publicationFormData(pub) });
  return data.publication;
};

export const updateMyPublication = async (id, pub) => {
  const data = await publicationRequest(`update-my-publication/${id}`, { method: 'POST', body: publicationFormData(pub) });
  return data.publication;
};

export const deleteMyPublication = async (id) => publicationRequest(`delete-my-publication/${id}`);

// ==================== h-index ====================
// h-index from citation counts of the member's DOI-linked papers; omit userId for your own.
export const fetchHIndex = async (userId) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/h-index${userId ? `/${userId}` : ''}`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
  });
  if (!response.ok) throw new Error(`h-index request failed (${response.status})`);
  return response.json(); // { hIndex, citations, papers, papersWithDoi }
};

// ==================== ORCID ====================
// Connect the member's ORCID iD and import their public works as "published elsewhere" papers.
export const fetchOrcidStatus = async () => publicationRequest('orcid/status');

// Returns ORCID's consent URL; ORCID sends the browser back to /profile?orcid=connected|cancelled|error
export const startOrcidConnect = async () => {
  const data = await publicationRequest(`orcid/connect?return=${encodeURIComponent(window.location.origin)}`);
  return data.authUrl;
};

export const disconnectOrcid = async () => publicationRequest('orcid/connection', { method: 'DELETE' });

export const fetchOrcidWorks = async () => publicationRequest('orcid/works');

export const importOrcidWorks = async (putCodes) => publicationRequest('orcid/import', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ putCodes })
});

// ==================== Auto-fill a publication (DOI / ORCID / Google Scholar export) ====================
// Each result has the add-publication form's fields plus `duplicate` (null, or the paper already on the profile).
export const lookupPublicationDoi = async (doi) =>
  publicationRequest(`publication-lookup/doi?doi=${encodeURIComponent(doi.trim())}`); // { publication, duplicate }

// Google Scholar / BibTeX / RIS / CSV export → { items: [...fields, duplicate], count }
export const parseCitationFile = async (file) => {
  const body = new FormData();
  body.append('file', file);
  return publicationRequest('publication-lookup/file', { method: 'POST', body });
};

export const checkPublicationDuplicate = async ({ title = '', url = '', except = null }) => {
  const qs = new URLSearchParams({ title, url, ...(except ? { except: String(except) } : {}) });
  const data = await publicationRequest(`publication-lookup/check?${qs}`);
  return data.duplicate;
};
